<script setup lang="ts">
import type { CompanionFileRef } from '../../../../stores/companion-auth'

import { computed } from 'vue'

/**
 * Whitelist renderer for message file attachments (companion-protocol.md §5).
 *
 * Inline-openable in chat: all images (incl. gif) as <img>, video as <video>,
 * audio as <audio> (click to play). Music cards (③'s play_music tool) embed the
 * provider's external player. Word/PPT/Excel show a document icon + download
 * link. Everything else shows a generic file icon + name + download.
 *
 * TODO(roadmap): 3D model preview (vrm / fbx / obj / glb) — planned, not yet
 * implemented; those fall into the generic download branch for now.
 */

const props = defineProps<{
  files: CompanionFileRef[]
}>()

type DisplayKind = 'image' | 'video' | 'audio' | 'music' | 'doc' | 'other'

interface DisplayFile {
  kind: DisplayKind
  url: string
  name: string
  icon: string
  // Only set for kind: 'music' — drives the embedded provider player.
  platform?: string
  id?: string | number
}

const EXT_OF = /\.([a-z0-9]+)(?:[?#]|$)/i

const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'avif'])
const VIDEO_EXTS = new Set(['mp4', 'webm', 'mov', 'mkv'])
const AUDIO_EXTS = new Set(['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac'])
// Office docs render as an icon + download (no inline preview).
const DOC_ICONS: Record<string, string> = {
  doc: 'i-mdi:file-word text-blue-500',
  docx: 'i-mdi:file-word text-blue-500',
  ppt: 'i-mdi:file-powerpoint text-orange-500',
  pptx: 'i-mdi:file-powerpoint text-orange-500',
  xls: 'i-mdi:file-excel text-green-600',
  xlsx: 'i-mdi:file-excel text-green-600',
}

function classify(file: CompanionFileRef): DisplayFile {
  const name = file.name || decodeURIComponent(file.url.split('/').pop()?.split('?')[0] || 'file')
  const ext = (name.match(EXT_OF)?.[1] || file.url.match(EXT_OF)?.[1] || '').toLowerCase()
  const mime = file.mime || ''

  // Music cards carry no downloadable asset — keep platform + id for the embed.
  if (file.kind === 'music')
    return { kind: 'music', url: file.url, name, icon: '', platform: file.platform, id: file.id }
  if (file.kind === 'image' || mime.startsWith('image/') || IMAGE_EXTS.has(ext))
    return { kind: 'image', url: file.url, name, icon: '' }
  if (file.kind === 'video' || mime.startsWith('video/') || VIDEO_EXTS.has(ext))
    return { kind: 'video', url: file.url, name, icon: '' }
  if (file.kind === 'audio' || mime.startsWith('audio/') || AUDIO_EXTS.has(ext))
    return { kind: 'audio', url: file.url, name, icon: '' }
  if (DOC_ICONS[ext])
    return { kind: 'doc', url: file.url, name, icon: DOC_ICONS[ext] }
  return { kind: 'other', url: file.url, name, icon: 'i-solar:file-bold-duotone' }
}

const displayFiles = computed(() => props.files.map(classify))
</script>

<template>
  <div v-if="displayFiles.length" :class="['mt-2', 'flex flex-col gap-2']">
    <template v-for="(file, i) in displayFiles" :key="`${file.url}-${i}`">
      <!-- images / gif: inline, click opens the original -->
      <a
        v-if="file.kind === 'image'"
        :href="file.url" target="_blank" rel="noopener"
        :class="['block max-w-full']"
      >
        <img
          :src="file.url" :alt="file.name" loading="lazy"
          :class="['max-h-60 max-w-full rounded-lg object-contain']"
        >
      </a>

      <!-- video: inline player -->
      <video
        v-else-if="file.kind === 'video'"
        :src="file.url" controls preload="metadata"
        :class="['max-h-60 max-w-full rounded-lg']"
      />

      <!-- audio: click to play -->
      <audio
        v-else-if="file.kind === 'audio'"
        :src="file.url" controls preload="none"
        :class="['w-full max-w-80']"
      />

      <!-- music card: embed the provider's external player (③'s play_music).
           NetEase's outchain player is not an S3 asset; it's iframed by id.
           auto=0 — browsers block iframe autoplay anyway, so this is a card
           the listener presses play on. Unknown platforms fall through to the
           click-through link below. -->
      <iframe
        v-else-if="file.kind === 'music' && file.platform === 'netease' && file.id != null"
        :src="`https://music.163.com/outchain/player?type=2&id=${file.id}&auto=0&height=66`"
        frameborder="0" marginwidth="0" marginheight="0"
        :class="['max-w-full rounded-lg']"
        width="330" height="86"
      />
      <a
        v-else-if="file.kind === 'music'"
        :href="file.url" target="_blank" rel="noopener"
        :class="[
          'flex items-center gap-2 rounded-lg px-3 py-2',
          'bg-neutral-200/50 dark:bg-neutral-700/50',
          'hover:bg-neutral-200/80 dark:hover:bg-neutral-700/80',
          'transition-colors max-w-80',
        ]"
      >
        <div :class="['i-solar:music-note-2-bold-duotone h-6 w-6 shrink-0 text-primary-500']" />
        <span :class="['truncate text-sm text-neutral-700 dark:text-neutral-200']">{{ file.name }}</span>
      </a>

      <!-- office docs & everything else: icon + name, click downloads -->
      <a
        v-else
        :href="file.url" :download="file.name" target="_blank" rel="noopener"
        :class="[
          'flex items-center gap-2 rounded-lg px-3 py-2',
          'bg-neutral-200/50 dark:bg-neutral-700/50',
          'hover:bg-neutral-200/80 dark:hover:bg-neutral-700/80',
          'transition-colors max-w-80',
        ]"
      >
        <div :class="[file.icon, 'h-6 w-6 shrink-0']" />
        <span :class="['truncate text-sm text-neutral-700 dark:text-neutral-200']">{{ file.name }}</span>
        <div :class="['i-solar:download-minimalistic-bold-duotone ml-auto h-4 w-4 shrink-0 text-neutral-500 dark:text-neutral-400']" />
      </a>
    </template>
  </div>
</template>
